import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogOut, IndianRupee, Package, MessageSquare, ShoppingCart, RefreshCw, Trash2, Plus, UploadCloud } from "lucide-react";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [coupons, setCoupons] = useState([]);

  const authed = () => {
    if (!localStorage.getItem("kspb_admin_token")) { nav("/admin/login"); return false; }
    return true;
  };

  const load = async () => {
    if (!authed()) return;
    try {
      const [d, p, o, e, c] = await Promise.all([
        api.get("/admin/dashboard"), api.get("/admin/products"),
        api.get("/admin/orders"), api.get("/admin/enquiries"),
        api.get("/admin/coupons"),
      ]);
      setData(d.data); setProducts(p.data); setOrders(o.data); setEnquiries(e.data); setCoupons(c.data);
    } catch {
      localStorage.removeItem("kspb_admin_token");
      nav("/admin/login");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = () => { localStorage.removeItem("kspb_admin_token"); nav("/admin/login"); };

  const updateOrderStatus = async (id, status_value) => {
    await api.patch(`/admin/orders/${id}/status?status_value=${status_value}`);
    toast.success("Updated");
    load();
  };

  const updateEnquiryStatus = async (id, status_value) => {
    await api.patch(`/admin/enquiries/${id}/status?status_value=${status_value}`);
    toast.success("Updated");
    load();
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const [showNew, setShowNew] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [np, setNp] = useState({ name: "", slug: "", category: "personal", short_description: "", description: "", features: "", image_url: "", price: "", installation_charge: "", monthly_subscription: "", yearly_subscription: "", warranty: "1 Year" });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = `${BACKEND_URL}${r.data.url}`;
      setNp((prev) => ({ ...prev, image_url: full }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const swapProductImage = async (productId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = `${BACKEND_URL}${r.data.url}`;
      await api.patch(`/admin/products/${productId}`, { image_url: full });
      toast.success("Product image updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const createProduct = async () => {
    try {
      await api.post("/admin/products", {
        ...np,
        price: np.price ? parseFloat(np.price) : null,
        installation_charge: parseFloat(np.installation_charge || 0),
        monthly_subscription: parseFloat(np.monthly_subscription || 0),
        yearly_subscription: parseFloat(np.yearly_subscription || 0),
        features: np.features.split("\n").filter((x) => x.trim()),
      });
      toast.success("Product added");
      setShowNew(false);
      setNp({ name: "", slug: "", category: "personal", short_description: "", description: "", features: "", image_url: "", price: "", installation_charge: "", monthly_subscription: "", yearly_subscription: "", warranty: "1 Year" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [newCoupon, setNewCoupon] = useState({ code: "", value: 10, discount_type: "percent", min_order: 0 });
  const createCoupon = async () => {
    try {
      await api.post("/admin/coupons", { ...newCoupon, code: newCoupon.code.toUpperCase(), value: parseFloat(newCoupon.value), min_order: parseFloat(newCoupon.min_order) });
      toast.success("Coupon created");
      setNewCoupon({ code: "", value: 10, discount_type: "percent", min_order: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportOrdersCSV = () => {
    const rows = [["Order#", "Customer", "Mobile", "Total", "Payment", "Status", "Date"]];
    orders.forEach((o) => rows.push([o.order_number, o.billing.full_name, o.billing.mobile, o.total, o.payment_status, o.status, o.created_at]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `kspb-orders-${Date.now()}.csv`; a.click();
  };

  if (!data) return <div className="p-14 text-slate-500">Loading admin…</div>;

  const stats = data.stats;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eyebrow">Admin</div>
          <h1 className="font-heading text-3xl font-bold text-[#0A192F] mt-2">Dashboard</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-sm"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0A192F] text-white text-sm"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Revenue", value: fmtINR(stats.revenue), icon: IndianRupee },
          { label: "Orders", value: stats.total_orders, icon: ShoppingCart },
          { label: "Enquiries", value: stats.total_enquiries, icon: MessageSquare },
          { label: "Products", value: stats.total_products, icon: Package },
        ].map((s) => (
          <div key={s.label} className="p-5 rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500">{s.label}</div>
              <s.icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="font-heading text-2xl font-bold text-[#0A192F] mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-8" data-testid="admin-tabs">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="coupons">Coupons</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="p-5 rounded-2xl border border-slate-200 bg-white">
            <h3 className="font-heading font-semibold text-[#0A192F] mb-3">Recent orders</h3>
            {data.recent_orders.map((o) => (
              <div key={o.id} className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                <span>{o.order_number} · {o.billing.full_name}</span>
                <span className="font-semibold">{fmtINR(o.total)}</span>
              </div>
            ))}
          </div>
          <div className="p-5 rounded-2xl border border-slate-200 bg-white">
            <h3 className="font-heading font-semibold text-[#0A192F] mb-3">Recent enquiries</h3>
            {data.recent_enquiries.map((e) => (
              <div key={e.id} className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                <span>{e.name} · {e.mobile}</span>
                <span className="capitalize text-slate-500">{e.enquiry_type}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="flex justify-end mb-3">
            <button onClick={exportOrdersCSV} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Export CSV</button>
          </div>
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{["Order#", "Customer", "Mobile", "Total", "Payment", "Status", ""].map((h) => <th key={h} className="p-3 font-semibold text-[#0A192F]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="p-3 font-mono text-xs">{o.order_number}</td>
                    <td className="p-3">{o.billing.full_name}</td>
                    <td className="p-3">{o.billing.mobile}</td>
                    <td className="p-3 font-semibold">{fmtINR(o.total)}</td>
                    <td className="p-3 capitalize">{o.payment_status}</td>
                    <td className="p-3 capitalize">{o.status}</td>
                    <td className="p-3">
                      <select value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)} className="h-8 px-2 rounded border border-slate-200 text-xs">
                        {["placed", "confirmed", "shipped", "delivered", "cancelled"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{["Name", "Mobile", "City", "Type", "Message", "Status"].map((h) => <th key={h} className="p-3 font-semibold text-[#0A192F]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="p-3">{e.name}</td>
                    <td className="p-3">{e.mobile}</td>
                    <td className="p-3">{e.city}</td>
                    <td className="p-3 capitalize">{e.enquiry_type}</td>
                    <td className="p-3 max-w-xs truncate">{e.message}</td>
                    <td className="p-3">
                      <select value={e.status} onChange={(ev) => updateEnquiryStatus(e.id, ev.target.value)} className="h-8 px-2 rounded border border-slate-200 text-xs">
                        {["new", "contacted", "closed"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-3">
            <button onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#FF6D00] text-white text-sm font-medium"><Plus className="w-4 h-4" /> Add product</button>
          </div>
          {showNew && (
            <div className="p-4 mb-4 rounded-xl border border-slate-200 bg-white grid grid-cols-1 md:grid-cols-2 gap-3">
              {["name", "slug", "category", "short_description", "warranty"].map((k) => (
                <input key={k} placeholder={k} value={np[k]} onChange={(e) => setNp({ ...np, [k]: e.target.value })} className="h-10 px-3 rounded border border-slate-200 text-sm" data-testid={`np-${k}`} />
              ))}
              {["price", "installation_charge", "monthly_subscription", "yearly_subscription"].map((k) => (
                <input key={k} type="number" placeholder={k} value={np[k]} onChange={(e) => setNp({ ...np, [k]: e.target.value })} className="h-10 px-3 rounded border border-slate-200 text-sm" data-testid={`np-${k}`} />
              ))}
              <div className="md:col-span-2 p-3 rounded border border-dashed border-slate-300 bg-slate-50">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0A192F] text-white text-sm font-medium cursor-pointer">
                    <UploadCloud className="w-4 h-4" />
                    {uploading ? "Uploading…" : "Upload Image (JPG / PNG)"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="np-upload-file" />
                  </label>
                  <span className="text-xs text-slate-500">Max 5 MB. You can also paste an image URL below.</span>
                </div>
                <input placeholder="image_url (auto-filled after upload, or paste manually)" value={np.image_url} onChange={(e) => setNp({ ...np, image_url: e.target.value })} className="mt-3 w-full h-10 px-3 rounded border border-slate-200 text-sm bg-white" data-testid="np-image_url" />
                {np.image_url && (
                  <div className="mt-3">
                    <img src={np.image_url} alt="preview" className="w-32 h-32 object-cover rounded-lg border border-slate-200" />
                  </div>
                )}
              </div>
              <textarea placeholder="description" value={np.description} onChange={(e) => setNp({ ...np, description: e.target.value })} className="md:col-span-2 p-3 rounded border border-slate-200 text-sm" rows={3} data-testid="np-description" />
              <textarea placeholder="features (one per line)" value={np.features} onChange={(e) => setNp({ ...np, features: e.target.value })} className="md:col-span-2 p-3 rounded border border-slate-200 text-sm" rows={3} data-testid="np-features" />
              <button onClick={createProduct} className="md:col-span-2 h-10 rounded-lg bg-[#FF6D00] text-white text-sm font-medium" data-testid="np-create">Create Product</button>
            </div>
          )}
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{["Image", "Name", "Category", "Price", "Stock", ""].map((h) => <th key={h} className="p-3 font-semibold text-[#0A192F]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="p-3">
                      <label className="cursor-pointer inline-block group relative">
                        <img src={p.image_url} alt="" className="w-14 h-14 rounded object-cover border border-slate-200 group-hover:opacity-70 transition-opacity" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 rounded text-white text-[10px] font-semibold">Change</div>
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => swapProductImage(p.id, e.target.files?.[0])} data-testid={`swap-image-${p.slug}`} />
                      </label>
                    </td>
                    <td className="p-3">{p.name}</td>
                    <td className="p-3 capitalize">{p.category}</td>
                    <td className="p-3">{p.price ? fmtINR(p.price) : "Quote"}</td>
                    <td className="p-3">{p.stock}</td>
                    <td className="p-3"><button onClick={() => deleteProduct(p.id)} className="text-red-600" data-testid={`delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="coupons" className="mt-6">
          <div className="p-4 mb-4 rounded-xl border border-slate-200 bg-white grid grid-cols-1 md:grid-cols-5 gap-3">
            <input placeholder="CODE" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })} className="h-10 px-3 rounded border border-slate-200 text-sm" />
            <select value={newCoupon.discount_type} onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })} className="h-10 px-3 rounded border border-slate-200 text-sm">
              <option value="percent">Percent</option><option value="flat">Flat</option>
            </select>
            <input type="number" placeholder="value" value={newCoupon.value} onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })} className="h-10 px-3 rounded border border-slate-200 text-sm" />
            <input type="number" placeholder="min order" value={newCoupon.min_order} onChange={(e) => setNewCoupon({ ...newCoupon, min_order: e.target.value })} className="h-10 px-3 rounded border border-slate-200 text-sm" />
            <button onClick={createCoupon} className="h-10 rounded-lg bg-[#FF6D00] text-white text-sm font-medium">Create Coupon</button>
          </div>
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{["Code", "Type", "Value", "Min Order", "Active"].map((h) => <th key={h} className="p-3 font-semibold text-[#0A192F]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="p-3 font-mono">{c.code}</td>
                    <td className="p-3">{c.discount_type}</td>
                    <td className="p-3">{c.value}</td>
                    <td className="p-3">{fmtINR(c.min_order)}</td>
                    <td className="p-3">{c.active ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
